import { useState, useMemo } from "react";
import { formatMomentDate } from "../../../utils/dateHelper";
import { PlusIcon, SettingIcon } from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import StatusChip from "../../../components/statusChip/StatusChip";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { TableSearch } from "../../../components/form/Fields";
import CustomTable from "../../../components/table/CustomTable";

const ListSection = ({
  data,
  isLoading,
  searchTerm,
  setSearchTerm,
  onAdd,
  onEdit,
  onView,
  onDelete,
  onChangePassword,
}) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const filteredData =
    data?.filter(
      (user) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.mobile?.includes(searchTerm),
    ) || [];

  const handleConfirmDeactivate = () => {
    if (selectedUser && typeof onDelete === "function") {
      onDelete(selectedUser.id);
      setShowStatusModal(false);
      setSelectedUser(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "sr_no",
        header: "SR NO.",
        cell: (_, row, index) => index + 1,
      },
      {
        id: "name",
        header: "NAME",
        cell: (_, row) => row.name,
      },
      {
        id: "email",
        header: "EMAIL",
        cell: (_, row) => row.email,
      },
      {
        id: "mobile",
        header: "MOBILE NUMBER",
        cell: (_, row) => row.mobile || "-",
      },
      {
        id: "role",
        header: "ROLE",
        cell: (_, row) => (
          <span className="badge bg-light text-dark border">
            {row.role?.name || "N/A"}
          </span>
        ),
      },
      {
        id: "status",
        header: "STATUS",
        cell: (_, row) => (
          <StatusChip
            bg={row.is_active ? "success" : "danger"}
            pill
            title={row.is_active ? "Active" : "Inactive"}
            className={row.is_system || !onDelete ? "" : "cursor-pointer"}
            onClick={() => {
              if (!row.is_system && onDelete) {
                setSelectedUser(row);
                setShowStatusModal(true);
              }
            }}
          />
        ),
      },
      {
        id: "created_at",
        header: "CREATED ON",
        cell: (_, row) =>
          row.createdAt ? formatMomentDate(row.createdAt, "DD-MM-YYYY hh:mm A") : "-",
      },
    ],
    [onDelete],
  );

  const actionList = useMemo(
    () =>
      [
        onView && {
          title: "View",
          handler: (row) => onView(row),
        },
        onEdit && {
          title: "Edit",
          isVisible: (row) => !row.is_system,
          handler: (row) => onEdit(row),
        },
        onChangePassword && {
          title: "Change Password",
          isVisible: (row) => !row.is_system,
          handler: (row) => onChangePassword(row),
          icon: <SettingIcon />,
        },
        onDelete && {
          title: (row) => (row.is_active ? "Deactivate" : "Activate"),
          isVisible: (row) => !row.is_system,
          handler: (row) => {
            setSelectedUser(row);
            setShowStatusModal(true);
          },
        },
      ].filter(Boolean),
    [onView, onEdit, onChangePassword, onDelete],
  );

  return (
    <>
      <div className="p-4 d-flex align-items-center justify-content-between gap-4">
        <TableSearch
          value={searchTerm}
          placeholder="Search"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {onAdd && (
          <CommonButton
            variant="primary"
            icon={<PlusIcon />}
            title="Add User"
            type="button"
            onClick={onAdd}
          />
        )}
      </div>

      <CustomTable
        data={filteredData}
        columns={columns}
        loading={isLoading}
        keyField="id"
        emptyMessage="No users found"
        actionList={actionList}
        isRowActionDisabled={(row) => row.is_system}
      />

      <ConfirmModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedUser(null);
        }}
        title={selectedUser?.is_active ? "Deactivate User" : "Activate User"}
        message={`Are you sure you want to ${selectedUser?.is_active ? "deactivate" : "activate"} this user?`}
        onConfirm={handleConfirmDeactivate}
      />
    </>
  );
};

export default ListSection;
